import { TimelineEvent } from "@deligoo/shared"
import clsx from "clsx"
import { memo, useState } from "react"
import { shallowEqual } from "react-redux"
import useAsyncEffect from "use-async-effect"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import panelCls from "@/components/Panel/Panel.module.scss"
import { EventsTimeline } from "@/modules/EventsTimeline"
import { setAboutPanel, setShowOrderDetails } from "@/store/ui"
import { Driver } from "@/types"
import { fetchDriverEventsById } from "@/utils"

type DriverHistoryProps = {
  driver: Driver
}

export const DriverHistory = memo(({ driver }: DriverHistoryProps) => {
  const events = useDriverTimeline(driver.id)

  const dispatch = useAppDispatch()

  return (
    <Card className={panelCls.card}>
      <Card.Section>
        <EventsTimeline
          events={events}
          textKey={(event) => event.description.support_text}
          parameters={(event, paramCls) => {
            if (!event.description?.support_parameters) return null

            return Object.entries(event.description.support_parameters).map(([key, value]) => (
              <div
                key={key}
                className={clsx(
                  paramCls,
                  key === "order_uid" && ["font-weight-bold", "text-gray-60", "cursor-pointer"]
                )}
                onClick={
                  key === "order_uid" && event.data["order_id"]
                    ? () => {
                        const orderId = Number(event.data.order_id)
                        if (orderId) {
                          dispatch(setShowOrderDetails({ id: orderId }))
                          dispatch(setAboutPanel({ id: orderId, type: "order" }))
                        }
                      }
                    : undefined
                }
              >
                {value}
              </div>
            ))
          }}
        />
      </Card.Section>
    </Card>
  )
})

function useDriverTimeline(driverId: Driver["id"]) {
  const [timeline, setTimeline] = useState<"loading" | "error" | TimelineEvent[]>("loading")

  const currentSite = useAppSelector((state) => state.global.currentSite, shallowEqual)
  const date = useAppSelector((state) => state.global.date)

  useAsyncEffect(
    async (isSubscribed) => {
      try {
        const {
          response,
          json: { data: events },
        } = await fetchDriverEventsById(driverId, date)
        if (!response.ok) throw response

        if (events && isSubscribed()) setTimeline(events)
      } catch (error) {
        if (isSubscribed()) setTimeline("error")
      }
    },
    [driverId, currentSite]
  )

  return timeline
}
